import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import ConfirmationModalDelete from "../../../../Components/Common/Modal/SaveChangesModal";
import useConvertQuote from "./useConvertQuote";

// Quote statuses are not covered by the shared renderStatus helper, so they are
// rendered here with the same badge classes the Orders listing uses.
const renderQuoteStatus = (status) => {
  switch (status) {
    case "CONVERTED":
      return <span className="payment-success">CONVERTED</span>;
    case "ACTIVE":
      return <span className="payment-pending">ACTIVE</span>;
    default:
      return status ? <div>{status}</div> : "-";
  }
};

// QOT rows return "entity_id"; the entity read response uses "entityid".
export const getQuoteRowId = (row = {}) => row.entity_id ?? row.entityid;

// A quote has no member until it is converted, and the field name differs by
// source, so take the first one present and fall back to a dash.
export const getQuoteMemberName = (row = {}) =>
  row.billing_name ||
  row.customer_name ||
  row.member_name ||
  row.prospect_name ||
  "-";

const QuoteActionCell = ({ quote }) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const { mutate: convertQuote, isPending: isConverting } = useConvertQuote();

  const handleConvertConfirm = () => {
    convertQuote({ quoteId: getQuoteRowId(quote) });
    setShowModal(false);
  };

  const canViewOrder = quote.status === "CONVERTED";
  const canConvert = quote.status === "ACTIVE";

  return (
    <>
      <div className="d-flex gap-4 align-items-center">
        {canViewOrder && (
          <Button
            className="primary-outline"
            onClick={() => navigate(`/order/orders/update/${quote.order_id}`)}
          >
            View Order
          </Button>
        )}
        {canConvert && (
          <Button
            className="primary-outline"
            disabled={isConverting}
            onClick={() => setShowModal(true)}
          >
            Convert
          </Button>
        )}
      </div>

      <ConfirmationModalDelete
        isOpen={showModal}
        message="Are you sure you want to convert this quote to an order?"
        onConfirm={handleConvertConfirm}
        onCancel={() => setShowModal(false)}
      />
    </>
  );
};

function useQuoteColumn(data) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleCheckboxChange = (entityid) => {
    setSelectedRows((prevSelectedRows = []) =>
      prevSelectedRows.includes(entityid)
        ? prevSelectedRows.filter((id) => id !== entityid)
        : [...prevSelectedRows, entityid]
    );
  };

  const handleSelectAllChange = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelectedRows(newSelectAll ? data?.map((row) => getQuoteRowId(row)) : []);
  };

  useEffect(() => {
    setSelectedRows([]);
    setSelectAll(false);
  }, [data]);

  useEffect(() => {
    setSelectAll(data?.length > 0 && selectedRows.length === data.length);
  }, [selectedRows, data?.length]);

  const quoteColumn = [
    {
      header: () => (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={handleSelectAllChange}
        />
      ),
      accessorKey: "checkbox",
      enableSorting: false,
      cell: (props) => (
        <div>
          <input
            type="checkbox"
            checked={selectedRows.includes(getQuoteRowId(props.row.original))}
            onChange={() => handleCheckboxChange(getQuoteRowId(props.row.original))}
          />
        </div>
      ),
      size: 40,
    },
    {
      header: "Code",
      accessorKey: "code",
      cell: (props) => (
        <Link
          className="table-drilling-link"
          to={`/order/quotes/update/${getQuoteRowId(props.row.original)}`}
        >
          {props?.getValue() ? props?.getValue() : "-"}
        </Link>
      ),
      size: 100,
    },
    {
      header: "Product",
      accessorKey: "product",
      cell: (props) => (props?.getValue() ? props?.getValue() : "-"),
      size: 200,
    },
    {
      header: "AG",
      accessorKey: "ag_name",
      cell: (props) => (props?.getValue() ? props?.getValue() : "-"),
      size: 200,
    },
    {
      header: "Prospect / Member",
      accessorKey: "billing_name",
      cell: (props) => getQuoteMemberName(props?.row?.original),
      size: 200,
    },
    {
      header: "Premium",
      accessorKey: "gross_amount",
      cell: (props) => (props?.getValue() ? "₹ " + props?.getValue() : "-"),
      size: 160,
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: (props) => renderQuoteStatus(props?.getValue()),
      size: 160,
    },
    {
      header: "Action",
      accessorKey: "action",
      cell: (props) => <QuoteActionCell quote={props.row.original} />,
    },
  ];

  return { quoteColumn, selectedRows };
}

export default useQuoteColumn;
